import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import AppContext from '../context/AppContext';
import AnnotationsContext from '../context/AnnotationsContext';
import CommentsAPI from '../api/CommentsAPI';
import { CommentModel } from '../types/CommentModel';
import {
  CommentComposer,
  CommentComposerHandle,
  CommentItem,
  ComposeTarget,
} from './CommentPieces';

// A two-level comment tree (top-level + replies) from an already-scoped list, with compose, reply and
// quote. `scopeMovePgn` attaches new comments to a move (null = game-level). Mutations go through
// AnnotationsContext so every consumer stays in sync.
export default function CommentTreeView(props: {
  gameId: number;
  comments: CommentModel[];
  scopeMovePgn: string | null;
  emptyText?: string;
  // Set when the composer was opened deliberately (e.g. "Add note"), so it takes focus.
  composeNote?: boolean;
}): React.ReactNode {
  const {
    gameId,
    comments,
    scopeMovePgn,
    emptyText = 'No notes yet.',
    composeNote = false,
  } = props;
  const { isAuthenticated, isWebmaster, openLogin } = useContext(AppContext);
  const { upsertComment, removeComment, reload, composerFocusNonce, consumeComposerFocus } =
    useContext(AnnotationsContext);
  const [replyTarget, setReplyTarget] = useState<ComposeTarget | null>(null);
  const mainComposerRef = useRef<CommentComposerHandle>(null);

  // A move's comment icon requests focus for that move's thread. consume returns true only for the
  // requested move, so ordinary navigation never steals focus.
  useEffect(() => {
    if (consumeComposerFocus(scopeMovePgn)) {
      mainComposerRef.current?.focus();
    }
  }, [composerFocusNonce, scopeMovePgn, consumeComposerFocus]);

  const { topLevel, repliesByParent } = useMemo(() => {
    const sorted = [...comments].sort(
      (a, b) => new Date(a.CreatedAt).getTime() - new Date(b.CreatedAt).getTime(),
    );
    const top = sorted.filter((c) => c.ParentId == null);
    const byParent = new Map<number, CommentModel[]>();
    for (const c of sorted) {
      if (c.ParentId != null) {
        const list = byParent.get(c.ParentId) ?? [];
        list.push(c);
        byParent.set(c.ParentId, list);
      }
    }
    return { topLevel: top, repliesByParent: byParent };
  }, [comments]);

  // Reply inline under the comment it answers. Replies attach to the top-level comment.
  const startReply = (comment: CommentModel) => {
    setReplyTarget({
      parentId: comment.ParentId ?? comment.Id,
      quotedCommentId: comment.Id,
      quotedExcerpt: comment.Body,
      quotedAuthorName: comment.AuthorName,
    });
  };

  // Quote: put the comment's text in the main composer as a markdown blockquote, then a blank line.
  const quoteIntoComposer = (comment: CommentModel) => {
    const quoted =
      comment.Body.split('\n')
        .map((line) => `> ${line}`)
        .join('\n') + '\n\n';
    mainComposerRef.current?.insertQuote(quoted);
  };

  const submitComment = async (target: ComposeTarget, body: string, isPrivate: boolean) => {
    const created = await CommentsAPI.create({
      GameId: gameId,
      MovePgn: scopeMovePgn,
      ParentId: target.parentId,
      QuotedCommentId: target.quotedCommentId ?? null,
      Body: body,
      IsPrivate: isPrivate,
    });
    upsertComment(created);
    setReplyTarget(null);
  };

  return (
    <div className="cm-comment-tree">
      {topLevel.length === 0 ? <div className="text-muted small mb-2">{emptyText}</div> : null}
      {topLevel.map((c) => {
        const replies = repliesByParent.get(c.Id) ?? [];
        // Replies exist only on the public thread, and only a webmaster can write one (server-enforced).
        const canReply =
          isWebmaster && c.Visibility === 'Public' && c.Status !== 'Removed';
        const replyingHere = replyTarget != null && replyTarget.parentId === c.Id;
        return (
          <div key={c.Id} className="cm-comment-group">
            <CommentItem
              comment={c}
              isReply={false}
              canReply={canReply}
              onReply={startReply}
              onQuote={quoteIntoComposer}
              onEdited={upsertComment}
              onDeleted={removeComment}
              onModerated={reload}
            />
            {replies.map((r) => (
              <CommentItem
                key={r.Id}
                comment={r}
                isReply={true}
                // Replying to a reply threads under the same parent (startReply resolves ParentId ?? Id).
                canReply={isWebmaster && r.Visibility === 'Public' && r.Status !== 'Removed'}
                onReply={startReply}
                onQuote={quoteIntoComposer}
                onEdited={upsertComment}
                onDeleted={removeComment}
                onModerated={reload}
              />
            ))}
            {replyingHere && isAuthenticated ? (
              <div className="cm-comment-reply">
                <CommentComposer
                  submitLabel="Reply"
                  autoFocus
                  quotedAuthorName={replyTarget?.quotedAuthorName}
                  quotedExcerpt={replyTarget?.quotedExcerpt}
                  onCancel={() => setReplyTarget(null)}
                  onSubmit={(body, isPrivate) => submitComment(replyTarget!, body, isPrivate)}
                />
              </div>
            ) : null}
          </div>
        );
      })}

      {isAuthenticated ? (
        <div className="cm-comment-new mt-2">
          {/* `composeNote` only says whether this composer was opened deliberately (focus it). */}
          <CommentComposer
            ref={mainComposerRef}
            submitLabel="Save note"
            autoFocus={composeNote}
            onSubmit={(body, isPrivate) => submitComment({ parentId: null }, body, isPrivate)}
          />
        </div>
      ) : (
        <div className="text-muted small mt-2">
          <button type="button" className="btn btn-link p-0 align-baseline" onClick={openLogin}>
            Log in
          </button>{' '}
          to keep private notes on this game.
        </div>
      )}
    </div>
  );
}
